import { type FC, Suspense, useCallback, useMemo, useState } from "react";
import { ErrorBoundary } from "react-error-boundary";
import { useNavigate } from "react-router-dom";
import type {
	MetaAdsCampaignListItem,
	MetaAdsConnectionStatus,
	MetaAdsCrossClientItem,
	MetaAdsOverviewItem,
} from "shared";
import { Icon } from "../common/Icon";
import { useCompany } from "../company/useCompany";
import { Card } from "../components/Card";
import { Select } from "../components/Select";
import { LinkGen } from "../LinkGen";
import { AppPage } from "../layout/AppLayout";
import {
	DateRangeSelector,
	getDefaultDateRange,
	persistDateRange,
} from "./components/DateRangeSelector";
import { MetaAdsApprovalSettings } from "./MetaAdsApprovalSettings";
import { useSwitchMetaAdsClient } from "./MetaAdsSwitchContext";
import { formatMetaAdsMoney } from "./metaAdsMoney";
import { useApprovalSettings, usePendingApprovalCount } from "./useApprovals";
import {
	useCampaignDraftActions,
	useCampaignDrafts,
} from "./useCampaignDrafts";
import { useMetaAdsAdminCheck } from "./useMetaAdsAdmin";
import { useMetaAdsCrossClientOverview } from "./useMetaAdsCrossClientOverview";
import { useMetaAdsOverview } from "./useMetaAdsOverview";
import { useCanEditMetaAds, useCanManageMetaAds } from "./useMyMetaAdsRole";

const DRAFT_PAGE_SIZE = 5;

type SortField =
	| "name"
	| "status"
	| "activity"
	| "spend"
	| "clicks"
	| "campaigns";
type SortDir = "asc" | "desc";

const formatCurrency = (val: number) =>
	`$${val.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const formatConnectionStatus = (status: MetaAdsConnectionStatus) => {
	switch (status) {
		case "connected":
			return "Connected";
		case "requires_reconnection":
			return "Requires Reconnection";
		default:
			return "Disconnected";
	}
};

const connectionStatusClass = (status: MetaAdsConnectionStatus) => {
	switch (status) {
		case "connected":
			return "bg-success";
		case "requires_reconnection":
			return "bg-warning text-dark";
		default:
			return "bg-secondary";
	}
};

const CrossClientMetrics: FC<{
	items: MetaAdsOverviewItem[];
	currentCompanyId: number;
	onSelectCompany: (id: number) => void;
	onNavigateToReporting: (companyId: number) => void;
}> = ({ items, currentCompanyId, onSelectCompany, onNavigateToReporting }) => {
	const [dateRange, setDateRange] = useState(getDefaultDateRange);
	const { data: metrics } = useMetaAdsCrossClientOverview(
		dateRange.from,
		dateRange.to,
	);

	const handleDateChange = useCallback((from: string, to: string) => {
		persistDateRange(from, to);
		setDateRange({ from, to });
	}, []);

	const [search, setSearch] = useState("");
	const [statusFilter, setStatusFilter] = useState<
		"all" | MetaAdsConnectionStatus
	>("connected");
	const [sortField, setSortField] = useState<SortField>("name");
	const [sortDir, setSortDir] = useState<SortDir>("asc");

	const metricsMap = useMemo(() => {
		const map = new Map<number, MetaAdsCrossClientItem>();
		for (const m of metrics) {
			map.set(m.companyId, m);
		}
		return map;
	}, [metrics]);

	const filtered = useMemo(() => {
		const q = search.toLowerCase();
		return items.filter((item) => {
			const matchesSearch = item.companyName.toLowerCase().includes(q);
			const matchesStatus =
				statusFilter === "all" || item.connectionStatus === statusFilter;
			return matchesSearch && matchesStatus;
		});
	}, [items, search, statusFilter]);

	const sorted = useMemo(() => {
		return [...filtered].sort((a, b) => {
			const dir = sortDir === "asc" ? 1 : -1;
			const am = metricsMap.get(a.companyId);
			const bm = metricsMap.get(b.companyId);
			switch (sortField) {
				case "name":
					return a.companyName.localeCompare(b.companyName) * dir;
				case "status":
					return (Number(b.connected) - Number(a.connected)) * dir;
				case "activity":
					return (
						(a.lastActivity ?? "").localeCompare(b.lastActivity ?? "") * dir
					);
				case "spend":
					return ((am?.totalSpend ?? 0) - (bm?.totalSpend ?? 0)) * dir;
				case "clicks":
					return ((am?.clicks ?? 0) - (bm?.clicks ?? 0)) * dir;
				case "campaigns":
					return (
						((am?.activeCampaigns ?? 0) - (bm?.activeCampaigns ?? 0)) * dir
					);
				default:
					return 0;
			}
		});
	}, [filtered, sortField, sortDir, metricsMap]);

	const handleSort = (field: SortField) => {
		if (sortField === field) {
			setSortDir((d) => (d === "asc" ? "desc" : "asc"));
		} else {
			setSortField(field);
			setSortDir(field === "name" ? "asc" : "desc");
		}
	};

	const sortIcon = (field: SortField) => {
		if (sortField !== field) return null;
		return (
			<Icon
				name={sortDir === "asc" ? "arrow_upward" : "arrow_downward"}
				size={14}
			/>
		);
	};

	// Totals for visible (filtered) companies only
	const totals = useMemo(() => {
		let totalSpend = 0;
		let totalImpressions = 0;
		let totalClicks = 0;
		let totalActiveCampaigns = 0;
		for (const item of filtered) {
			const m = metricsMap.get(item.companyId);
			if (!m) continue;
			totalSpend += m.totalSpend;
			totalImpressions += m.impressions;
			totalClicks += m.clicks;
			totalActiveCampaigns += m.activeCampaigns;
		}
		return {
			totalSpend,
			totalImpressions,
			totalClicks,
			avgCtr: totalImpressions > 0 ? (totalClicks / totalImpressions) * 100 : 0,
			avgCpc: totalClicks > 0 ? totalSpend / totalClicks : 0,
			totalActiveCampaigns,
		};
	}, [filtered, metricsMap]);

	return (
		<Card>
			<Card.Header>
				All Companies
				<div
					style={{
						marginLeft: "auto",
						display: "flex",
						gap: 8,
						alignItems: "center",
						flexWrap: "wrap",
					}}
				>
					<DateRangeSelector
						from={dateRange.from}
						to={dateRange.to}
						onChange={handleDateChange}
					/>
					<Select
						className="form-select form-select-sm"
						style={{ width: 180 }}
						value={statusFilter}
						onChange={(e) =>
							setStatusFilter(e.target.value as "all" | MetaAdsConnectionStatus)
						}
					>
						<option value="all">All statuses</option>
						<option value="connected">Connected</option>
						<option value="requires_reconnection">Requires Reconnection</option>
						<option value="disconnected">Disconnected</option>
					</Select>
					<input
						type="text"
						placeholder="Search companies..."
						value={search}
						onChange={(e) => setSearch(e.target.value)}
						style={{ maxWidth: 250 }}
						className="form-control form-control-sm"
					/>
				</div>
			</Card.Header>
			<Card.Body style={{ padding: 0, overflowX: "auto" }}>
				<table className="table" style={{ marginBottom: 0 }}>
					<thead>
						<tr>
							<th
								style={{ cursor: "pointer" }}
								onClick={() => handleSort("name")}
							>
								Company {sortIcon("name")}
							</th>
							<th
								style={{ cursor: "pointer" }}
								onClick={() => handleSort("status")}
							>
								Status {sortIcon("status")}
							</th>
							<th>Ad Account</th>
							<th
								style={{ cursor: "pointer" }}
								onClick={() => handleSort("activity")}
							>
								Last Activity {sortIcon("activity")}
							</th>
							<th
								style={{ cursor: "pointer" }}
								onClick={() => handleSort("spend")}
							>
								Spend {sortIcon("spend")}
							</th>
							<th>Impressions</th>
							<th
								style={{ cursor: "pointer" }}
								onClick={() => handleSort("clicks")}
							>
								Clicks {sortIcon("clicks")}
							</th>
							<th>Avg CTR</th>
							<th>Avg CPC</th>
							<th
								style={{ cursor: "pointer" }}
								onClick={() => handleSort("campaigns")}
							>
								Active Camp. {sortIcon("campaigns")}
							</th>
							<th />
						</tr>
					</thead>
					<tbody>
						{sorted.map((item) => {
							const m = metricsMap.get(item.companyId);
							return (
								<tr
									key={item.companyId}
									style={{
										cursor: "pointer",
										background:
											item.companyId === currentCompanyId
												? "var(--bs-primary-bg-subtle, #e8f0fe)"
												: undefined,
									}}
									onClick={() => onSelectCompany(item.companyId)}
								>
									<td>
										<strong>{item.companyName}</strong>
										{item.companyId === currentCompanyId && (
											<span
												className="badge bg-primary"
												style={{ marginLeft: 8 }}
											>
												Current
											</span>
										)}
									</td>
									<td>
										<span
											className={`badge ${connectionStatusClass(item.connectionStatus)}`}
										>
											{formatConnectionStatus(item.connectionStatus)}
										</span>
									</td>
									<td>
										{item.adAccountName ?? "-"}
										{item.adAccountId && (
											<div className="text-muted" style={{ fontSize: 11 }}>
												{item.adAccountId}
											</div>
										)}
									</td>
									<td>
										{item.lastActivity
											? new Date(item.lastActivity).toLocaleDateString()
											: "-"}
									</td>
									<td>{m ? formatCurrency(m.totalSpend) : "-"}</td>
									<td>{m ? m.impressions.toLocaleString() : "-"}</td>
									<td>{m ? m.clicks.toLocaleString() : "-"}</td>
									<td>{m ? `${m.avgCtr.toFixed(2)}%` : "-"}</td>
									<td>{m ? formatCurrency(m.avgCpc) : "-"}</td>
									<td>{m ? m.activeCampaigns : "-"}</td>
									<td>
										{item.connected && (
											<button
												type="button"
												className="btn btn-sm btn-outline-primary"
												onClick={(e) => {
													e.stopPropagation();
													onNavigateToReporting(item.companyId);
												}}
											>
												<Icon name="bar_chart" size={14} />
											</button>
										)}
									</td>
								</tr>
							);
						})}
						{sorted.length === 0 && (
							<tr>
								<td colSpan={9} style={{ textAlign: "center" }}>
									No companies found
								</td>
							</tr>
						)}
					</tbody>
					<tfoot>
						<tr style={{ fontWeight: "bold" }}>
							<td>Total</td>
							<td />
							<td />
							<td />
							<td>{formatCurrency(totals.totalSpend)}</td>
							<td>{totals.totalImpressions.toLocaleString()}</td>
							<td>{totals.totalClicks.toLocaleString()}</td>
							<td>{totals.avgCtr.toFixed(2)}%</td>
							<td>{formatCurrency(totals.avgCpc)}</td>
							<td>{totals.totalActiveCampaigns}</td>
							<td />
						</tr>
					</tfoot>
				</table>
			</Card.Body>
		</Card>
	);
};

const DraftsTable: FC = () => {
	const navigate = useNavigate();
	const { data: drafts } = useCampaignDrafts();
	const { data: approvalSettings } = useApprovalSettings();
	const { deleteDraft, publishDraft } = useCampaignDraftActions();
	const canEdit = useCanEditMetaAds();
	const approvalEnabled = approvalSettings?.enabled ?? false;
	const [page, setPage] = useState(0);

	if (drafts.length === 0) {
		return (
			<Card>
				<Card.Header>Campaign Drafts</Card.Header>
				<Card.Body>
					<p className="text-muted" style={{ marginBottom: 0 }}>
						{canEdit
							? "No drafts yet. Create your first campaign!"
							: "No campaign drafts to show."}
					</p>
				</Card.Body>
			</Card>
		);
	}

	const totalPages = Math.max(1, Math.ceil(drafts.length / DRAFT_PAGE_SIZE));
	const safePage = Math.min(page, totalPages - 1);
	const pageDrafts = drafts.slice(
		safePage * DRAFT_PAGE_SIZE,
		(safePage + 1) * DRAFT_PAGE_SIZE,
	);

	return (
		<Card>
			<Card.Header>Campaign Drafts</Card.Header>
			<Card.Body style={{ padding: 0 }}>
				<table className="table" style={{ marginBottom: 0 }}>
					<thead>
						<tr>
							<th>Name</th>
							<th>Budget</th>
							<th>Created</th>
							<th>Status</th>
							{canEdit && <th>Actions</th>}
						</tr>
					</thead>
					<tbody>
						{pageDrafts.map((draft: MetaAdsCampaignListItem) => {
							const isPendingApproval = draft.status === "PENDING_APPROVAL";
							return (
								<tr key={draft.id}>
									<td>
										{canEdit && !isPendingApproval ? (
											<button
												type="button"
												className="btn btn-link p-0 text-start"
												onClick={() =>
													navigate(LinkGen.MetaAds.DraftEdit(draft.id))
												}
											>
												{draft.name || "(unnamed)"}
											</button>
										) : (
											draft.name || "(unnamed)"
										)}
									</td>
									<td>
										{draft.budgetType} -{" "}
										{formatMetaAdsMoney(draft.budgetAmount)}
									</td>
									<td>{new Date(draft.createdAt).toLocaleDateString()}</td>
									<td>
										{isPendingApproval ? (
											<span className="badge bg-warning text-dark">
												Pending Approval
											</span>
										) : (
											<span className="badge bg-secondary">Draft</span>
										)}
									</td>
									{canEdit && (
										<td>
											<div style={{ display: "flex", gap: 8 }}>
												<button
													type="button"
													className="btn btn-sm btn-outline-primary"
													onClick={() =>
														navigate(LinkGen.MetaAds.DraftEdit(draft.id))
													}
													disabled={isPendingApproval}
												>
													Edit
												</button>
												<button
													type="button"
													className="btn btn-sm btn-primary"
													onClick={() => publishDraft.mutate(draft.id)}
													disabled={
														publishDraft.isPending ||
														isPendingApproval ||
														approvalEnabled
													}
													title={
														approvalEnabled
															? "Submit for approval from the campaign wizard instead"
															: undefined
													}
												>
													Publish
												</button>
												<button
													type="button"
													className="btn btn-sm btn-outline-danger"
													onClick={() => deleteDraft.mutate(draft.id)}
													disabled={deleteDraft.isPending || isPendingApproval}
												>
													Delete
												</button>
											</div>
										</td>
									)}
								</tr>
							);
						})}
					</tbody>
				</table>
				{totalPages > 1 && (
					<div
						style={{
							display: "flex",
							justifyContent: "center",
							gap: 8,
							padding: 12,
						}}
					>
						<button
							type="button"
							className="btn btn-sm btn-outline-secondary"
							disabled={safePage === 0}
							onClick={() => setPage((p) => Math.max(0, p - 1))}
						>
							Previous
						</button>
						<span style={{ alignSelf: "center" }}>
							Page {safePage + 1} of {totalPages}
							<span className="text-muted"> · {drafts.length} drafts</span>
						</span>
						<button
							type="button"
							className="btn btn-sm btn-outline-secondary"
							disabled={safePage >= totalPages - 1}
							onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
						>
							Next
						</button>
					</div>
				)}
			</Card.Body>
		</Card>
	);
};

const ApprovalsButton: FC = () => {
	const navigate = useNavigate();
	const { data: pendingCount } = usePendingApprovalCount();
	return (
		<button
			type="button"
			className="btn btn-outline-primary"
			style={{ position: "relative" }}
			onClick={() => navigate(LinkGen.MetaAds.Approvals())}
		>
			<Icon name="fact_check" size={18} /> Approvals
			{(pendingCount ?? 0) > 0 && (
				<span
					className="badge bg-danger"
					style={{
						position: "absolute",
						top: -6,
						right: -6,
						fontSize: 11,
					}}
				>
					{pendingCount}
				</span>
			)}
		</button>
	);
};

export const PageMetaAds: FC = () => {
	const company = useCompany();
	const { switchClient } = useSwitchMetaAdsClient();
	const { data: overview } = useMetaAdsOverview();
	const { data: adminCheck } = useMetaAdsAdminCheck();
	const navigate = useNavigate();
	const canEdit = useCanEditMetaAds();
	const canManage = useCanManageMetaAds();

	const currentOverview = overview.find(
		(item) => item.companyId === company.id,
	);
	const isConnected = currentOverview?.connected ?? false;

	const handleSelectCompany = (companyId: number) => {
		const option = overview.find((item) => item.companyId === companyId);
		switchClient(companyId, { name: option?.companyName });
	};

	const handleNavigateToReporting = (companyId: number) => {
		const option = overview.find((item) => item.companyId === companyId);
		const switched = switchClient(companyId, { name: option?.companyName });
		if (switched || companyId === company.id) {
			navigate(LinkGen.MetaAds.Reporting());
		}
	};

	return (
		<AppPage title="Meta Ads">
			<div style={{ display: "flex", flexDirection: "column", gap: 24 }}>
				<ErrorBoundary
					fallback={
						<Card>
							<Card.Header>All Companies</Card.Header>
							<Card.Body>
								<p className="text-muted" style={{ marginBottom: 0 }}>
									Unable to load cross-client metrics.
								</p>
							</Card.Body>
						</Card>
					}
				>
					<Suspense fallback={<p>Loading overview...</p>}>
						<CrossClientMetrics
							items={overview}
							currentCompanyId={company.id}
							onSelectCompany={handleSelectCompany}
							onNavigateToReporting={handleNavigateToReporting}
						/>
					</Suspense>
				</ErrorBoundary>

				<Card>
					<Card.Header>Current Company: {company.name}</Card.Header>
					<Card.Body>
						{(() => {
							if (!currentOverview) return <p>No data available.</p>;
							return (
								<div>
									<p>
										<strong>Status:</strong>{" "}
										<span
											className={`badge ${connectionStatusClass(currentOverview.connectionStatus)}`}
										>
											{formatConnectionStatus(currentOverview.connectionStatus)}
										</span>
									</p>
									{currentOverview.adAccountName && (
										<p>
											<strong>Ad Account:</strong>{" "}
											{currentOverview.adAccountName}
											{currentOverview.adAccountId && (
												<span className="text-muted">
													{" "}
													({currentOverview.adAccountId})
												</span>
											)}
										</p>
									)}
									{currentOverview.connectionStatus === "disconnected" && (
										<p className="text-muted">
											Meta Ads is disconnected. Reporting data remains
											read-only.
										</p>
									)}
									{currentOverview.connectionStatus ===
										"requires_reconnection" &&
										canManage && (
											<p className="text-muted">
												Reconnect Meta Ads in Settings → Apps to resume campaign
												management.
											</p>
										)}
									{!currentOverview.connected &&
										currentOverview.connectionStatus === "disconnected" &&
										canManage && (
											<p className="text-muted">
												Go to Settings &gt; Apps to connect Meta Ads for this
												company.
											</p>
										)}
								</div>
							);
						})()}
					</Card.Body>
				</Card>

				{currentOverview && (
					<div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
						<button
							type="button"
							className="btn btn-primary"
							onClick={() => navigate(LinkGen.MetaAds.Reporting())}
						>
							<Icon name="bar_chart" size={18} /> View Reporting
						</button>
						{isConnected && (
							<>
								{canEdit && (
									<button
										type="button"
										className="btn btn-outline-primary"
										onClick={() => navigate(LinkGen.MetaAds.CampaignCreate())}
									>
										<Icon name="add" size={18} /> Create Campaign
									</button>
								)}
								<button
									type="button"
									className="btn btn-outline-primary"
									onClick={() => navigate(LinkGen.MetaAds.Leads())}
								>
									<Icon name="group" size={18} /> View Leads
								</button>
								<button
									type="button"
									className="btn btn-outline-primary"
									onClick={() => navigate(LinkGen.MetaAds.Audiences())}
								>
									<Icon name="group" size={18} /> Audiences
								</button>
								{canEdit && (
									<button
										type="button"
										className="btn btn-outline-primary"
										onClick={() => navigate(LinkGen.MetaAds.Reports())}
									>
										<Icon name="summarize" size={18} /> Reports
									</button>
								)}
								{canEdit && (
									<button
										type="button"
										className="btn btn-outline-primary"
										onClick={() =>
											navigate(LinkGen.MetaAds.CampaignTemplates())
										}
									>
										<Icon name="file_copy" size={18} /> Templates
									</button>
								)}
								{canManage && (
									<button
										type="button"
										className="btn btn-outline-primary"
										onClick={() => navigate(LinkGen.MetaAds.Team())}
									>
										<Icon name="group_add" size={18} /> Team
									</button>
								)}
								{adminCheck?.isAdmin && (
									<button
										type="button"
										className="btn btn-outline-secondary"
										onClick={() => navigate(LinkGen.MetaAds.AdminConnections())}
									>
										<Icon name="admin_panel_settings" size={18} /> Admin
									</button>
								)}
								<ApprovalsButton />
							</>
						)}
					</div>
				)}

				{isConnected && (
					<ErrorBoundary
						fallback={
							<Card>
								<Card.Header>Campaign Drafts</Card.Header>
								<Card.Body>
									<p className="text-muted" style={{ marginBottom: 0 }}>
										Unable to load drafts. You may not have permission to view
										campaigns.
									</p>
								</Card.Body>
							</Card>
						}
					>
						<Suspense fallback={<p>Loading drafts...</p>}>
							<DraftsTable />
						</Suspense>
					</ErrorBoundary>
				)}

				{isConnected && <MetaAdsApprovalSettings />}
			</div>
		</AppPage>
	);
};
